import { normalizeMetaGraphId } from "@common/utils/metaPartnershipAdCreative";
import type { CreativeBlock } from "../guidedFlow/types";
import type { FacebookPage, InstagramAccount } from "../types";
import { mergeInstagramAccountsWithPageScopedFirst } from "./mergeInstagramAccounts";

/** Linked IG on the page row from the identity catalog — no Meta round-trip. */
export function resolveInstagramAccountForAdvertiserPage(
  page: FacebookPage,
  catalog: readonly InstagramAccount[]
): InstagramAccount | null {
  const linkedId = page.linked_instagram_user_id?.trim();
  if (!linkedId) {
    return null;
  }
  const existing = catalog.find((account) => account.id === linkedId);
  if (existing) {
    return existing;
  }
  const username = page.linked_instagram_username?.trim().replace(/^@+/, "") ?? "";
  return {
    id: linkedId,
    name: username || page.name,
    username,
    followers_count: 0,
    profile_picture_url: "",
  };
}

export function canDefaultAdvertiserInstagramFromPageCatalog(
  page: FacebookPage
): boolean {
  return Boolean(page.linked_instagram_user_id?.trim());
}

/** Copy linked IG ids onto a page row when Meta returns actors but `/ads/pages` omitted them. */
export function enrichFacebookPageWithLinkedInstagram(
  page: FacebookPage,
  account: InstagramAccount | null | undefined
): FacebookPage {
  const linkedId = account?.id?.trim();
  if (!linkedId || page.linked_instagram_user_id?.trim()) {
    return page;
  }
  const username = account?.username?.trim().replace(/^@+/, "");
  return {
    ...page,
    linked_instagram_user_id: linkedId,
    ...(username ? { linked_instagram_username: username } : {}),
  };
}

/** Copy linked IG ids from the catalog page row when the select value omitted them. */
export function facebookPageWithCatalogLinkedMetadata(
  page: FacebookPage,
  catalogPages: readonly FacebookPage[]
): FacebookPage {
  const catalogPage = catalogPages.find((row) => row.id === page.id);
  if (!catalogPage) {
    return page;
  }
  const linkedId = catalogPage.linked_instagram_user_id?.trim();
  const linkedUsername =
    catalogPage.linked_instagram_username?.trim().replace(/^@+/, "") ?? "";
  if (!linkedId && !linkedUsername) {
    return page;
  }
  return {
    ...page,
    ...(linkedId ? { linked_instagram_user_id: linkedId } : {}),
    ...(linkedUsername ? { linked_instagram_username: linkedUsername } : {}),
  };
}

export function enrichFacebookPageWithDeclaredLinkedInstagram(
  page: FacebookPage,
  accounts: readonly InstagramAccount[]
): FacebookPage {
  const linkedId = page.linked_instagram_user_id?.trim();
  const linkedUsername =
    page.linked_instagram_username?.trim().replace(/^@+/, "") ?? "";
  if (!linkedId && !linkedUsername) {
    return page;
  }
  const normalizedLinkedId = linkedId ? normalizeMetaGraphId(linkedId) : "";
  const fromId = normalizedLinkedId
    ? accounts.find(
        (account) => normalizeMetaGraphId(account.id) === normalizedLinkedId
      )
    : undefined;
  const fromUsername =
    !fromId && linkedUsername
      ? accounts.find(
          (account) =>
            account.username?.trim().replace(/^@+/, "") === linkedUsername
        )
      : undefined;
  const linked = fromId ?? fromUsername;
  return linked ? enrichFacebookPageWithLinkedInstagram(page, linked) : page;
}

/**
 * When the page row lacks linked IG metadata, infer it from the first
 * page-scoped fetch row (backend returns page-linked actors first).
 */
export function facebookPageWithResolvedLinkedInstagram(
  page: FacebookPage,
  fetchedAccounts: readonly InstagramAccount[],
  options?: { inferFromFirstFetchedWhenUndeclared?: boolean }
): FacebookPage {
  const withDeclared = enrichFacebookPageWithDeclaredLinkedInstagram(
    page,
    fetchedAccounts
  );
  if (withDeclared.linked_instagram_user_id?.trim()) {
    return withDeclared;
  }
  if (!options?.inferFromFirstFetchedWhenUndeclared) {
    return withDeclared;
  }
  const first = fetchedAccounts[0];
  return first ? enrichFacebookPageWithLinkedInstagram(withDeclared, first) : withDeclared;
}

export function instagramAccountAfterAdvertiserFacebookPageSelected(args: {
  page: FacebookPage | null;
  catalogAccounts: readonly InstagramAccount[];
  fetchedAccounts?: readonly InstagramAccount[];
  instagramClearDisabled?: boolean;
  /** When true (guided create), select the page-linked IG from catalog metadata. */
  autoSelectLinkedInstagram?: boolean;
  /** @deprecated Page-scoped fetch removed; use {@link autoSelectLinkedInstagram}. */
  defaultToFirstFetchedWhenUnlinked?: boolean;
  inferLinkedFromFirstFetch?: boolean;
}): InstagramAccount | null {
  if (args.instagramClearDisabled || !args.page) {
    return null;
  }

  if (args.autoSelectLinkedInstagram) {
    const linkedFromCatalog = resolveInstagramAccountForAdvertiserPage(
      args.page,
      args.catalogAccounts
    );
    if (linkedFromCatalog) {
      return linkedFromCatalog;
    }
  }

  const fetched = args.fetchedAccounts ?? [];
  if (fetched.length > 0 && args.defaultToFirstFetchedWhenUnlinked) {
    return fetched[0] ?? null;
  }

  return null;
}

/**
 * Immediate Instagram value when the user picks a Facebook page (catalog metadata only;
 * page-scoped fetch may refine this in {@link resolveAdvertiserInstagramAfterPageFetch}).
 */
export function resolveAdvertiserInstagramOnFacebookPagePicked(args: {
  page: FacebookPage;
  catalogPages: readonly FacebookPage[];
  catalogAccounts: readonly InstagramAccount[];
  instagramClearDisabled?: boolean;
  autoSelectLinkedInstagram?: boolean;
}): { page: FacebookPage; instagramAccount: InstagramAccount | null } {
  const page = facebookPageWithCatalogLinkedMetadata(
    args.page,
    args.catalogPages
  );
  if (args.instagramClearDisabled) {
    return { page, instagramAccount: null };
  }
  return {
    page,
    instagramAccount: instagramAccountAfterAdvertiserFacebookPageSelected({
      page,
      catalogAccounts: args.catalogAccounts,
      instagramClearDisabled: args.instagramClearDisabled,
      autoSelectLinkedInstagram: args.autoSelectLinkedInstagram,
    }),
  };
}

export type ResolveAdvertiserInstagramAfterPageFetchResult =
  | { action: "skip" }
  | { action: "set"; instagramAccount: InstagramAccount | null };

/**
 * After a page-scoped Instagram fetch (or on Facebook page change before fetch completes),
 * resolve the advertiser Instagram for the creative row.
 *
 * - Auto-fill: page-linked actor, then first fetched row in create mode, else null.
 * - Manual override on this page: canonicalize the selected id only.
 */
export function resolveAdvertiserInstagramAfterPageFetch(args: {
  page: FacebookPage;
  creative: CreativeBlock;
  catalogAccounts: readonly InstagramAccount[];
  fetchedAccounts: readonly InstagramAccount[];
  userOverrodeForPage: boolean;
  instagramClearDisabled?: boolean;
  autoSelectLinkedInstagram?: boolean;
  defaultToFirstFetchedWhenUnlinked?: boolean;
}): ResolveAdvertiserInstagramAfterPageFetchResult {
  if (args.instagramClearDisabled) {
    return { action: "skip" };
  }

  const mergedAccounts = mergeInstagramAccountsWithPageScopedFirst(
    args.fetchedAccounts,
    args.catalogAccounts
  );

  if (args.userOverrodeForPage) {
    if (!args.creative.instagramAccount?.id) {
      return { action: "skip" };
    }
    return {
      action: "set",
      instagramAccount: creativeWithResolvedInstagramRowOnly(
        args.creative,
        mergedAccounts
      ).instagramAccount,
    };
  }

  const savedInstagramId = args.creative.instagramAccount?.id?.trim() ?? "";
  if (savedInstagramId) {
    const canonical = creativeWithResolvedInstagramRowOnly(
      args.creative,
      mergedAccounts
    ).instagramAccount;
    if (canonical?.id === savedInstagramId) {
      return { action: "set", instagramAccount: canonical };
    }
    if (mergedAccounts.some((account) => account.id === savedInstagramId)) {
      return { action: "set", instagramAccount: canonical };
    }
  }

  const nextIg = instagramAccountAfterAdvertiserFacebookPageSelected({
    page: args.page,
    catalogAccounts: mergedAccounts,
    fetchedAccounts: args.fetchedAccounts,
    instagramClearDisabled: args.instagramClearDisabled,
    autoSelectLinkedInstagram: args.autoSelectLinkedInstagram,
    defaultToFirstFetchedWhenUnlinked: Boolean(
      args.defaultToFirstFetchedWhenUnlinked
    ),
  });

  if (!nextIg) {
    return { action: "skip" };
  }

  return {
    action: "set",
    instagramAccount: nextIg,
  };
}

/** Whether the page-scoped sync effect should wait for fetch (edit hydration). */
export function shouldDeferAdvertiserInstagramPageSync(args: {
  pageIdChanged: boolean;
  fetchedAccountsEmpty: boolean;
  hasSelectedInstagram: boolean;
  defaultToFirstFetchedWhenUnlinked: boolean;
  userOverrodeForPage: boolean;
}): boolean {
  if (args.userOverrodeForPage && !args.hasSelectedInstagram) {
    return true;
  }
  return (
    !args.pageIdChanged &&
    args.fetchedAccountsEmpty &&
    args.hasSelectedInstagram &&
    !args.defaultToFirstFetchedWhenUnlinked
  );
}

/** Minimal patch when advertiser page / Instagram identity changes; null when unchanged. */
export function buildAdvertiserInstagramIdentityPatch(args: {
  page: FacebookPage;
  creative: CreativeBlock;
  instagramAccount: InstagramAccount | null;
}): Partial<CreativeBlock> | null {
  const prevIg = args.creative.instagramAccount;
  const nextIg = args.instagramAccount;
  const enrichedPage = nextIg
    ? enrichFacebookPageWithLinkedInstagram(args.page, nextIg)
    : args.page;
  const patch: Partial<CreativeBlock> = {};
  const pageIdsMatch =
    (args.page.id?.trim() ?? "") !== "" &&
    (args.page.id?.trim() ?? "") === (args.creative.fbPage?.id?.trim() ?? "");

  if (pageIdsMatch && enrichedPage !== args.creative.fbPage) {
    patch.fbPage = enrichedPage;
  }
  if (
    (nextIg?.id ?? "") !== (prevIg?.id ?? "") ||
    (nextIg?.username ?? "") !== (prevIg?.username ?? "") ||
    (nextIg?.name ?? "") !== (prevIg?.name ?? "")
  ) {
    patch.instagramAccount = nextIg;
  }

  return Object.keys(patch).length > 0 ? patch : null;
}

export function resolveAdvertiserInstagramForCreativeBlock(args: {
  fbPage: FacebookPage | null | undefined;
  instagramIdFromWire?: string | null;
  catalogAccounts: readonly InstagramAccount[];
}): InstagramAccount | null {
  const wireId = args.instagramIdFromWire?.trim();
  if (!wireId) {
    return null;
  }
  return (
    args.catalogAccounts.find((account) => account.id === wireId) ?? null
  );
}

/**
 * When Meta returns Instagram actors for the selected Facebook page(s), align form
 * creatives: use the canonical row when the selected id is in the list, default to
 * the first row when there is a page but no actor, and leave creatives alone when
 * the selected id is missing from the list (stale / other page).
 */
export function creativesWithSyncedInstagram(
  creatives: CreativeBlock[],
  accounts: InstagramAccount[]
): CreativeBlock[] {
  if (accounts.length === 0) return creatives;

  const defaultActor = accounts[0];

  return creatives.map((c) => {
    if (!c?.fbPage?.id) return c;

    const cur = String(c.instagramAccount?.id ?? "").trim();
    if (cur) {
      const row = accounts.find((ig) => ig.id === cur);
      if (!row) return c;
      if (
        c.instagramAccount?.username === row.username &&
        c.instagramAccount?.name === row.name
      ) {
        return c;
      }
      return { ...c, instagramAccount: row };
    }

    return { ...c, instagramAccount: defaultActor };
  });
}

/** Same rules as {@link creativesWithSyncedInstagram} for a single creative row. */
export function creativeWithSyncedInstagram(
  creative: CreativeBlock,
  accounts: InstagramAccount[]
): CreativeBlock {
  return creativesWithSyncedInstagram([creative], accounts)[0];
}

/**
 * After a page-scoped Instagram fetch, replace the selected account with the canonical
 * row from `accounts` when ids match. Does **not** choose a default account when none
 * is selected (used by edit surfaces where Facebook-only is valid).
 */
export function creativeWithResolvedInstagramRowOnly(
  creative: CreativeBlock,
  accounts: InstagramAccount[]
): CreativeBlock {
  const cur = String(creative.instagramAccount?.id ?? "").trim();
  if (!cur) return creative;
  const row = accounts.find((ig) => ig.id === cur);
  if (!row) return creative;
  if (
    creative.instagramAccount?.username === row.username &&
    creative.instagramAccount?.name === row.name
  ) {
    return creative;
  }
  return { ...creative, instagramAccount: row };
}

/** Same as {@link creativeWithResolvedInstagramRowOnly} for each creative row. */
export function creativesWithResolvedInstagramRowOnly(
  creatives: CreativeBlock[],
  accounts: InstagramAccount[]
): CreativeBlock[] {
  return creatives.map((c) => creativeWithResolvedInstagramRowOnly(c, accounts));
}

export function creativesInstagramSelectionEquals(
  a: CreativeBlock[],
  b: CreativeBlock[]
): boolean {
  if (a.length !== b.length) return false;
  return a.every((row, i) => {
    const x = b[i];
    return (
      (row?.instagramAccount?.id ?? "") === (x?.instagramAccount?.id ?? "") &&
      (row?.instagramAccount?.username ?? "") ===
        (x?.instagramAccount?.username ?? "")
    );
  });
}

export function instagramAccountsForSelectOptions(
  accounts: readonly InstagramAccount[],
  selected: InstagramAccount | null | undefined
): InstagramAccount[] {
  const selectedId = selected?.id?.trim();
  if (!selectedId) {
    return [...accounts];
  }
  if (accounts.some((account) => account.id === selectedId)) {
    return [...accounts];
  }
  return selected ? [selected, ...accounts] : [...accounts];
}

export function instagramSelectOptionsFromAccounts(
  accounts: readonly InstagramAccount[],
  selected?: InstagramAccount | null
): { label: string; value: InstagramAccount }[] {
  return instagramAccountsForSelectOptions(accounts, selected).map(
    (account) => ({
      label: account.username || account.name || "",
      value: account,
    })
  );
}
